/**
 * AdminGolfSignatures — Golf Signature Studio (admin-only).
 *
 * Why this exists: the 7 long-form Golf Signatures (putting, driving,
 * irons, pressure, caddie, bunker_mastery, caddie_role) are ~20-min
 * subliminal audios baked from a hardcoded affirmation list. Tweaking
 * the copy used to mean editing `scripts/regenerate_golf_signatures_v3.py`
 * and re-running it. This page lets Michael:
 *   1. See every signature with last-rendered timestamp + file size
 *   2. ▶ Preview the current audio
 *   3. Edit the affirmation lines + pause length
 *   4. Hit "Re-render in V3" — server kicks off ElevenLabs + ffmpeg
 *      mix in the background, page polls status until done
 *
 * Route: /admin/golf/signatures
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { AdminLayout } from "../AdminPages";
import {
  ArrowLeft,
  Pencil,
  Save,
  RotateCw,
  Loader2,
  Check,
  AlertTriangle,
  X,
  Play,
  Pause,
  Plus,
  Trash2,
  Clock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const fmtAgo = (iso) => {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
};

export default function AdminGolfSignatures() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [sigs, setSigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {key, affirmations:[], pause_between_ms, original}
  const [savingKey, setSavingKey] = useState(null);
  const [renderingKey, setRenderingKey] = useState(null); // key currently being re-rendered
  const [renderStatus, setRenderStatus] = useState({}); // key → {status, log_tail, error}
  const [playingKey, setPlayingKey] = useState(null);
  const audioRef = useRef(null);
  const [flash, setFlash] = useState(null);

  // ─── Bulk re-render queue ───────────────────────────────────────
  // Frontend-driven sequential runner — admin checks N signatures,
  // hits "Re-render selected", we POST /render one at a time and poll
  // /status until done before kicking off the next. Keeps ElevenLabs
  // / ffmpeg from being hammered in parallel (also avoids OOM on the
  // tight container).
  // queueStatus[key] ∈ {queued | running | done | failed}
  const [selected, setSelected] = useState(new Set());
  const [queueStatus, setQueueStatus] = useState({});
  const [queueRunning, setQueueRunning] = useState(false);
  const queueAbortRef = useRef(false);

  const toggleSelected = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(sigs.map((s) => s.key)));
  const clearSelected = () => setSelected(new Set());
  const clearQueueResults = () => setQueueStatus({});

  // ── data loaders ──────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/golf/signatures`, { headers: auth(), cache: "no-store" });
      if (r.ok) {
        const data = await r.json();
        setSigs(data.signatures || []);
      } else if (r.status === 403) {
        setFlash({ kind: "err", text: "Admin access required." });
      }
    } catch (e) {
      setFlash({ kind: "err", text: "Failed to load signatures." });
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // ── render polling ────────────────────────────────────────────
  useEffect(() => {
    if (!renderingKey) return;
    const id = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/admin/golf/signatures/${renderingKey}/status`, {
          headers: auth(), cache: "no-store",
        });
        if (r.ok) {
          const j = await r.json();
          setRenderStatus((prev) => ({ ...prev, [renderingKey]: j }));
          if (j.status === "done" || j.status === "failed") {
            clearInterval(id);
            setRenderingKey(null);
            setFlash({
              kind: j.status === "done" ? "ok" : "err",
              text: j.status === "done"
                ? "Re-render complete · file written"
                : `Render failed: ${j.error || "unknown error"}`,
            });
            load(); // refresh file mtime + size
          }
        }
      } catch (_) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(id);
  }, [renderingKey]);

  // ── edit handlers ─────────────────────────────────────────────
  const startEdit = (sig) => {
    setEditing({
      key: sig.key,
      affirmations: [...sig.affirmations],
      pause_between_ms: sig.pause_between_ms,
      original: { affirmations: [...sig.affirmations], pause_between_ms: sig.pause_between_ms },
    });
  };
  const cancelEdit = () => setEditing(null);

  const isDirty = editing && (
    JSON.stringify(editing.affirmations) !== JSON.stringify(editing.original.affirmations)
    || editing.pause_between_ms !== editing.original.pause_between_ms
  );

  const saveEdit = async () => {
    if (!editing || !isDirty) return;
    setSavingKey(editing.key);
    try {
      const r = await fetch(`${API}/api/admin/golf/signatures/${editing.key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({
          affirmations: editing.affirmations.map((a) => a.trim()).filter(Boolean),
          pause_between_ms: editing.pause_between_ms,
        }),
      });
      if (r.ok) {
        setFlash({ kind: "ok", text: "Copy saved. Hit Re-render to push it live." });
        setEditing(null);
        load();
      } else {
        const err = await r.json().catch(() => ({}));
        setFlash({ kind: "err", text: `Save failed: ${err.detail || r.status}` });
      }
    } catch (_) {
      setFlash({ kind: "err", text: "Save failed (network)." });
    }
    setSavingKey(null);
  };

  const startRender = async (key) => {
    setRenderingKey(key);
    setRenderStatus((prev) => ({ ...prev, [key]: { status: "queued", log_tail: [] } }));
    try {
      const r = await fetch(`${API}/api/admin/golf/signatures/${key}/render`, {
        method: "POST", headers: auth(),
      });
      if (!r.ok) {
        setRenderingKey(null);
        const err = await r.json().catch(() => ({}));
        setFlash({ kind: "err", text: `Could not start render: ${err.detail || r.status}` });
      }
    } catch (_) {
      setRenderingKey(null);
      setFlash({ kind: "err", text: "Could not start render (network)." });
    }
  };

  // ── Bulk queue runner ─────────────────────────────────────────
  // Sequentially POSTs /render then polls /status until each one
  // finishes. Mirrors the queueAbortRef pattern from AdminGolfScripts
  // so the admin can hit "Stop queue" mid-batch.
  const pollUntilDone = (key) =>
    new Promise((resolve) => {
      const id = setInterval(async () => {
        if (queueAbortRef.current) {
          clearInterval(id);
          return resolve({ status: "failed", error: "aborted by user" });
        }
        try {
          const r = await fetch(`${API}/api/admin/golf/signatures/${key}/status`, {
            headers: auth(), cache: "no-store",
          });
          if (r.ok) {
            const j = await r.json();
            // Surface log lines into renderStatus too so the per-row log
            // panel keeps showing live progress during a bulk run.
            setRenderStatus((prev) => ({ ...prev, [key]: j }));
            if (j.status === "done" || j.status === "failed") {
              clearInterval(id);
              resolve(j);
            }
          }
        } catch (_) { /* keep polling */ }
      }, 2000);
    });

  const startBulkRender = async () => {
    if (selected.size === 0 || queueRunning) return;
    setQueueRunning(true);
    queueAbortRef.current = false;
    const keys = Array.from(selected);
    // Seed every selected row as "queued" up front so the UI shows
    // the full pipeline before the runner reaches them.
    setQueueStatus(Object.fromEntries(keys.map((k) => [k, "queued"])));
    setFlash(null);

    for (const key of keys) {
      if (queueAbortRef.current) break;
      setQueueStatus((prev) => ({ ...prev, [key]: "running" }));
      setRenderingKey(key); // also light up the single-row UI
      try {
        const r = await fetch(`${API}/api/admin/golf/signatures/${key}/render`, {
          method: "POST", headers: auth(),
        });
        if (!r.ok) {
          setQueueStatus((prev) => ({ ...prev, [key]: "failed" }));
          continue;
        }
        const final = await pollUntilDone(key);
        setQueueStatus((prev) => ({ ...prev, [key]: final.status }));
      } catch (_) {
        setQueueStatus((prev) => ({ ...prev, [key]: "failed" }));
      }
    }
    setRenderingKey(null);
    setQueueRunning(false);
    queueAbortRef.current = false;
    await load(); // refresh file mtimes + sizes
    setFlash({ kind: "ok", text: `Bulk re-render complete · ${keys.length} signature(s)` });
  };

  const abortBulkRender = () => {
    queueAbortRef.current = true;
    setFlash({ kind: "err", text: "Aborting after current signature finishes…" });
  };

  // ── preview audio ─────────────────────────────────────────────
  const togglePlay = (sig) => {
    if (playingKey === sig.key) {
      audioRef.current?.pause();
      setPlayingKey(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(`${API}${sig.audio_url}`);
    a.addEventListener("ended", () => setPlayingKey(null));
    a.addEventListener("error", () => {
      setPlayingKey(null);
      setFlash({ kind: "err", text: "Preview failed to load." });
    });
    a.play().catch(() => setPlayingKey(null));
    audioRef.current = a;
    setPlayingKey(sig.key);
  };

  // ── access gate ───────────────────────────────────────────────
  if (user?.role !== "admin") {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <AlertTriangle size={32} className="mx-auto text-amber-500 mb-3" />
          <p className="text-[#78716C]">Admin access required.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-golf-signatures">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl text-[#292524]">Golf Signature Studio</h2>
            <p className="text-sm text-[#78716C]">
              7 long-form Golf Signatures · Michael V3 voice · 20-min subliminal mix.
              Edit the affirmation copy, then hit Re-render to push new audio live.
            </p>
          </div>
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-sm text-[#78716C] hover:text-[#292524]"
            data-testid="golf-signatures-back-btn"
          >
            <ArrowLeft size={16} /> Back
          </button>
        </div>

        {flash && (
          <div
            className={`rounded-xl px-4 py-3 text-sm flex items-center justify-between ${
              flash.kind === "ok"
                ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                : "bg-red-50 text-red-800 border border-red-200"
            }`}
            data-testid="golf-signatures-flash"
          >
            <div className="flex items-center gap-2">
              {flash.kind === "ok" ? <Check size={14} /> : <AlertTriangle size={14} />}
              <span>{flash.text}</span>
            </div>
            <button onClick={() => setFlash(null)} className="opacity-60 hover:opacity-100">
              <X size={14} />
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-[#78716C]" data-testid="golf-signatures-loading">
            <Loader2 size={20} className="inline animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            {/* Bulk toolbar — sequential queue, mirrors AdminGolfScripts */}
            <div
              className="flex items-center justify-between rounded-xl bg-[#F5F5F4] border border-[#E6E2DD] px-4 py-2.5 text-xs text-[#78716C]"
              data-testid="golf-signatures-bulk-toolbar"
            >
              <div className="flex items-center gap-3">
                <button
                  onClick={selectAll}
                  disabled={queueRunning}
                  className="hover:text-[#292524] disabled:opacity-40"
                  data-testid="golf-signatures-select-all"
                >
                  Select all
                </button>
                <span className="text-[#D6D3D1]">·</span>
                <button
                  onClick={clearSelected}
                  disabled={queueRunning || selected.size === 0}
                  className="hover:text-[#292524] disabled:opacity-40"
                  data-testid="golf-signatures-clear-selected"
                >
                  Clear
                </button>
                {Object.keys(queueStatus).length > 0 && !queueRunning && (
                  <>
                    <span className="text-[#D6D3D1]">·</span>
                    <button
                      onClick={clearQueueResults}
                      className="hover:text-[#292524]"
                      data-testid="golf-signatures-clear-queue-results"
                    >
                      Clear queue results
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span data-testid="golf-signatures-selected-count">
                  {selected.size} selected
                </span>
                {queueRunning ? (
                  <button
                    onClick={abortBulkRender}
                    className="rounded-full bg-red-50 hover:bg-red-100 text-red-700 px-3 py-1.5 font-medium flex items-center gap-1.5"
                    data-testid="golf-signatures-abort-bulk"
                  >
                    <X size={12} /> Stop queue
                  </button>
                ) : (
                  <button
                    onClick={startBulkRender}
                    disabled={selected.size === 0 || renderingKey !== null}
                    className="rounded-full bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 font-medium flex items-center gap-1.5 disabled:opacity-40"
                    data-testid="golf-signatures-bulk-render"
                  >
                    <RotateCw size={12} /> Re-render selected
                  </button>
                )}
              </div>
            </div>

            {sigs.map((sig) => {
              const status = renderStatus[sig.key];
              const qStatus = queueStatus[sig.key]; // queued | running | done | failed
              const isRendering = renderingKey === sig.key || status?.status === "running" || status?.status === "queued" || qStatus === "running" || qStatus === "queued";
              const isEditing = editing?.key === sig.key;
              const isChecked = selected.has(sig.key);
              return (
                <div
                  key={sig.key}
                  className="rounded-2xl border border-[#E6E2DD] bg-white px-5 py-4 space-y-3"
                  data-testid={`golf-signature-row-${sig.key}`}
                >
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={queueRunning}
                        onChange={() => toggleSelected(sig.key)}
                        className="mt-1 h-4 w-4 accent-amber-500 cursor-pointer disabled:opacity-40"
                        data-testid={`golf-signature-checkbox-${sig.key}`}
                        aria-label={`Select ${sig.title} for bulk re-render`}
                      />
                      <div className="min-w-0">
                        <div className="font-medium text-[#292524] truncate flex items-center gap-2">
                          {sig.title}
                          {qStatus && (
                            <QueuePip status={qStatus} testid={`golf-signature-queue-pip-${sig.key}`} />
                          )}
                        </div>
                        <div className="text-xs text-[#78716C] mt-0.5 flex items-center gap-2 flex-wrap">
                          <span>{sig.affirmation_count} lines</span>
                          <span>·</span>
                          <span>pause {sig.pause_between_ms}ms</span>
                          <span>·</span>
                          <span>
                            {sig.file.exists
                              ? `${sig.file.size_mb}MB · ${fmtAgo(sig.file.mtime_iso)}`
                              : "no audio yet"}
                          </span>
                          {sig.has_override && (
                            <>
                              <span>·</span>
                              <span className="text-amber-600">edited copy</span>
                          </>
                        )}
                      </div>
                    </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      {sig.file.exists && (
                        <button
                          onClick={() => togglePlay(sig)}
                          className="rounded-full bg-[#F5F5F4] hover:bg-[#E6E2DD] p-2"
                          aria-label="preview"
                          title="Preview audio"
                          data-testid={`golf-signature-play-${sig.key}`}
                        >
                          {playingKey === sig.key ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                      )}
                      {!isEditing && (
                        <button
                          onClick={() => startEdit(sig)}
                          disabled={isRendering}
                          className="rounded-full bg-[#F5F5F4] hover:bg-[#E6E2DD] p-2 disabled:opacity-40"
                          aria-label="edit"
                          title="Edit copy"
                          data-testid={`golf-signature-edit-${sig.key}`}
                        >
                          <Pencil size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => startRender(sig.key)}
                        disabled={isRendering || isEditing}
                        className="rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-40"
                        data-testid={`golf-signature-render-${sig.key}`}
                      >
                        {isRendering ? (
                          <><Loader2 size={12} className="animate-spin" /> Rendering…</>
                        ) : (
                          <><RotateCw size={12} /> Re-render V3</>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Render progress log */}
                  {isRendering && status?.log_tail?.length > 0 && (
                    <div
                      className="rounded-lg bg-[#FAF7F2] border border-amber-200 text-xs text-[#78716C] px-3 py-2 font-mono space-y-0.5 max-h-32 overflow-y-auto"
                      data-testid={`golf-signature-log-${sig.key}`}
                    >
                      {status.log_tail.slice(-8).map((line, i) => (
                        <div key={i} className="truncate">{line}</div>
                      ))}
                    </div>
                  )}

                  {/* Edit panel */}
                  {isEditing && (
                    <EditPanel
                      editing={editing}
                      setEditing={setEditing}
                      saving={savingKey === editing.key}
                      isDirty={isDirty}
                      onSave={saveEdit}
                      onCancel={cancelEdit}
                    />
                  )}
                </div>
              );
            })}
            {sigs.length === 0 && (
              <div className="text-center py-12 text-[#78716C] text-sm">
                No signatures configured yet.
                <div className="mt-1 flex items-center justify-center gap-1 text-xs">
                  <Clock size={12} /> seed via the regenerate_golf_signatures_v3.py script
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

/** Tiny visual pip showing the current bulk-queue state of a row. */
function QueuePip({ status, testid }) {
  const map = {
    queued:  { label: "queued",  cls: "bg-[#F5F5F4] text-[#78716C]" },
    running: { label: "rendering", cls: "bg-amber-100 text-amber-800 animate-pulse" },
    done:    { label: "done",    cls: "bg-emerald-100 text-emerald-800" },
    failed:  { label: "failed",  cls: "bg-red-100 text-red-700" },
  };
  const cfg = map[status];
  if (!cfg) return null;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${cfg.cls}`}
      data-testid={testid}
    >
      {cfg.label}
    </span>
  );
}

/** Inline affirmation editor — appears under the signature row when editing. */
function EditPanel({ editing, setEditing, saving, isDirty, onSave, onCancel }) {
  const updateLine = (idx, value) => {
    setEditing((prev) => ({
      ...prev,
      affirmations: prev.affirmations.map((a, i) => (i === idx ? value : a)),
    }));
  };
  const addLine = () => {
    setEditing((prev) => ({
      ...prev,
      affirmations: [...prev.affirmations, ""],
    }));
  };
  const removeLine = (idx) => {
    setEditing((prev) => ({
      ...prev,
      affirmations: prev.affirmations.filter((_, i) => i !== idx),
    }));
  };

  return (
    <div className="rounded-xl bg-[#FAFAF9] border border-[#E6E2DD] p-4 space-y-3" data-testid={`golf-signature-edit-panel-${editing.key}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-[#78716C]">
          Affirmation copy · {editing.affirmations.length} lines
        </div>
        <label className="text-xs text-[#78716C] flex items-center gap-2">
          Pause between (ms)
          <input
            type="number"
            min={1500}
            max={15000}
            step={100}
            value={editing.pause_between_ms ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, pause_between_ms: Number(e.target.value) || null }))}
            className="w-20 rounded border border-[#D6D3D1] px-2 py-1 text-xs bg-white"
            data-testid={`golf-signature-pause-input-${editing.key}`}
          />
        </label>
      </div>

      <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
        {editing.affirmations.map((line, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <span className="text-xs text-[#A8A29E] w-5 shrink-0">{idx + 1}</span>
            <input
              value={line}
              onChange={(e) => updateLine(idx, e.target.value)}
              className="flex-1 rounded-lg border border-[#D6D3D1] px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
              data-testid={`golf-signature-line-${editing.key}-${idx}`}
            />
            <button
              onClick={() => removeLine(idx)}
              disabled={editing.affirmations.length <= 1}
              className="text-[#A8A29E] hover:text-red-500 disabled:opacity-30"
              aria-label="remove line"
              data-testid={`golf-signature-remove-${editing.key}-${idx}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-1">
        <button
          onClick={addLine}
          className="text-xs flex items-center gap-1 text-amber-700 hover:text-amber-800"
          data-testid={`golf-signature-add-line-${editing.key}`}
        >
          <Plus size={12} /> Add line
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="rounded-full text-xs px-3 py-1.5 text-[#78716C] hover:text-[#292524]"
            data-testid={`golf-signature-edit-cancel-${editing.key}`}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!isDirty || saving}
            className="rounded-full bg-[#292524] hover:bg-black text-white text-xs px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-40"
            data-testid={`golf-signature-edit-save-${editing.key}`}
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save copy
          </button>
        </div>
      </div>
    </div>
  );
}
